// MUNYA OS · Fichaje · chip topbar · M4.6 · C2b Face ID + geo en la app iOS
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Clock, Play, Stop, Pause, MapPin } from '@phosphor-icons/react';
import { api } from '../../lib/api.js';
import { captureLocation, confirmBiometric, isNativeApp } from '../../lib/native.js';

const KIND_LABEL: Record<string, string> = {
  check_in: 'Entrada',
  check_out: 'Salida',
  pause_start: 'Pausa',
  pause_end: 'Reanudar',
};

interface State {
  last_entry: { kind: string; occurred_at: string } | null;
  today: {
    presence_minutes: number;
    pause_minutes: number;
    effective_minutes: number;
    is_open: boolean;
  };
}

function fmtMin(m: number): string {
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${h}h ${String(min).padStart(2, '0')}m`;
}

export function FichajeChip(): ReactElement {
  const [state, setState] = useState<State | null>(null);
  const [fetchedAt, setFetchedAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState(false);
  const [acting, setActing] = useState(false);
  // C2b · opt-in de geolocalización (L5): null = aún sin cargar; solo aplica en la app
  const [geoOptIn, setGeoOptIn] = useState<boolean | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // C2b · al abrir el popover dentro de la app, cargar el estado de consentimiento geo
  useEffect(() => {
    if (!open || !isNativeApp() || geoOptIn !== null) return;
    api
      .fichajeGeoConsent()
      .then((r) => setGeoOptIn(r.geo_fichaje_opt_in))
      .catch(() => setGeoOptIn(false));
  }, [open, geoOptIn]);

  // R5 responsive · en táctil no hay mouseleave: cerrar con tap fuera y con Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const load = (): void => {
    api
      .fichajeMe()
      .then((s) => {
        setState(s);
        setFetchedAt(Date.now());
      })
      .catch((e) => console.warn('[munya] silent error', e));
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  // #108 · tick local: avanza el contador en cliente entre refetches (30s, sin red)
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // #108 · contador en vivo: el backend incluye el tramo abierto hasta el fetch;
  // aquí sumamos lo transcurrido desde entonces. En pausa la efectiva no avanza.
  const liveToday = useMemo(() => {
    if (!state) return null;
    if (!state.today.is_open || fetchedAt === 0) return state.today;
    const elapsed = Math.max(0, Math.floor((now - fetchedAt) / 60000));
    if (elapsed === 0) return state.today;
    const paused = state.last_entry?.kind === 'pause_start';
    return {
      ...state.today,
      presence_minutes: state.today.presence_minutes + elapsed,
      pause_minutes: state.today.pause_minutes + (paused ? elapsed : 0),
      effective_minutes: state.today.effective_minutes + (paused ? 0 : elapsed),
    };
  }, [state, fetchedAt, now]);

  const clock = async (
    kind: 'check_in' | 'check_out' | 'pause_start' | 'pause_end',
  ): Promise<void> => {
    // C2b · en la app iOS: confirmar identidad con Face ID/código ANTES de fichar
    // (registro horario TIME-001); si el usuario cancela, no se ficha.
    const ok = await confirmBiometric(
      `Confirmar ${KIND_LABEL[kind] ?? kind} · registro horario MUNYA`,
    );
    if (!ok) return;
    setActing(true);
    try {
      // C2b · coords solo con opt-in explícito (el backend re-valida el consentimiento)
      const loc = geoOptIn === true ? await captureLocation() : undefined;
      await api.fichajeClock(kind, loc);
      load();
    } catch (e) {
      // L8 · si sesión > 12h, backend devuelve 401 STEP_UP_REQUIRED
      if (e instanceof Error && /STEP_UP_REQUIRED|step-up|step_up/i.test(e.message)) {
        alert(
          'Sesión > 12h · vuelve a iniciar sesión antes de fichar (integridad TIME-001, Art. 35 ET).',
        );
        window.location.href = '/auth/login?next=' + encodeURIComponent(window.location.pathname);
      } else {
        throw e;
      }
    } finally {
      setActing(false);
    }
  };

  const isOpen = state?.today.is_open ?? false;
  const lastKind = state?.last_entry?.kind ?? null;

  return (
    <div className="relative" ref={rootRef}>
      <button
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1 h-[2.25rem] px-[0.75rem] lg:h-6 lg:px-2 rounded text-xs leading-none transition ${isOpen ? 'bg-[var(--color-munya-olive)] text-white' : 'opacity-60 hover:opacity-100 hover:bg-[var(--color-munya-sand)]/40'}`}
        title="Fichaje"
      >
        <Clock size={12} weight={isOpen ? 'fill' : 'regular'} />
        <span className="whitespace-nowrap">
          {liveToday ? fmtMin(liveToday.effective_minutes) : '—'}
        </span>
      </button>
      {open && (
        <div
          className="absolute right-0 top-10 lg:top-7 z-50 bg-[var(--color-munya-cream)] text-[var(--color-munya-coffee)] border border-[var(--color-munya-sand)] rounded-lg shadow-2xl w-64 max-lg:w-[16rem] max-w-[calc(100vw-1rem)] p-3"
          onMouseLeave={() => setOpen(false)}
        >
          <p className="text-[10px] uppercase tracking-wider opacity-60 mb-2">Hoy</p>
          <div className="grid grid-cols-2 gap-1 text-xs mb-3">
            <span className="opacity-60">Presencia:</span>
            <span className="text-right font-medium">
              {liveToday ? fmtMin(liveToday.presence_minutes) : '—'}
            </span>
            <span className="opacity-60">Pausas:</span>
            <span className="text-right">{liveToday ? fmtMin(liveToday.pause_minutes) : '—'}</span>
            <span className="opacity-60">Efectiva:</span>
            <span className="text-right font-bold">
              {liveToday ? fmtMin(liveToday.effective_minutes) : '—'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {!isOpen ? (
              <button
                onClick={() => void clock('check_in')}
                disabled={acting}
                className="col-span-2 px-2 py-1.5 text-xs rounded-md bg-[var(--color-munya-olive)] text-white flex items-center justify-center gap-1 disabled:opacity-50"
              >
                <Play size={12} weight="fill" /> Entrada
              </button>
            ) : (
              <>
                {lastKind === 'pause_start' ? (
                  <button
                    onClick={() => void clock('pause_end')}
                    disabled={acting}
                    className="col-span-2 px-2 py-1.5 text-xs rounded-md bg-[var(--color-munya-warning)] text-[var(--color-munya-coffee)] flex items-center justify-center gap-1 disabled:opacity-50"
                  >
                    <Play size={12} /> Reanudar
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => void clock('pause_start')}
                      disabled={acting}
                      className="px-2 py-1.5 text-xs rounded-md border border-[var(--color-munya-warning)] text-[var(--mn-state-delivery-fg)] hover:bg-[var(--mn-state-delivery-bg)] flex items-center justify-center gap-1 disabled:opacity-50"
                    >
                      <Pause size={12} /> Pausa
                    </button>
                    <button
                      onClick={() => void clock('check_out')}
                      disabled={acting}
                      className="px-2 py-1.5 text-xs rounded-md bg-[var(--color-munya-coffee)] text-[var(--color-munya-cream)] flex items-center justify-center gap-1 disabled:opacity-50"
                    >
                      <Stop size={12} weight="fill" /> Salida
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          {/* C2b · opt-in RGPD de ubicación en fichajes · solo en la app iOS */}
          {isNativeApp() && geoOptIn !== null && (
            <label className="flex items-center gap-1.5 mt-2.5 text-[11px] cursor-pointer select-none opacity-80">
              <input
                type="checkbox"
                checked={geoOptIn}
                onChange={(e) => {
                  const next = e.target.checked;
                  setGeoOptIn(next);
                  api.fichajeGeoConsentSet(next).catch(() => setGeoOptIn(!next));
                }}
                className="accent-[var(--color-munya-olive)]"
              />
              <MapPin size={11} />
              Registrar ubicación al fichar
            </label>
          )}
          <p className="text-[10px] opacity-40 mt-2 text-center">Fichaje TIME-001 · HMAC chain</p>
        </div>
      )}
    </div>
  );
}
